// client/src/components/admin/AdminDatabaseTools.js
// Admin interface for database seeding and resetting operations
// Supports simple mode for deterministic test accounts (alice@gmail.com/alice, bob@gmail.com/bob, etc.)
import { useState, useCallback } from 'react'
import useAuth from '../../hooks/useAuth'
import useAdminTool from '../../hooks/useAdminTool'
import PageLayout from '../common/layout/PageLayout'
import AdminToolPanel from './tools/AdminToolPanel'
import SeedOptionsForm from './tools/SeedOptionsForm'
import ConfirmActionButton from './tools/ConfirmActionButton'
import ActionButton from './tools/ActionButton'
import './AdminDatabaseTools.css'
import { MATCH_FORMAT } from '../../constants/matchFormats'

function AdminDatabaseTools() {
  const { isAdmin, loading: authLoading, user } = useAuth()

  // Form state for seed options
  const [seedOptions, setSeedOptions] = useState({
    tag: 'staging',
    withCompleted: true,
    withScheduled: true,
    withAvailability: true,
    completedPercent: 25,
    scheduledPercent: 25,
    availabilityPerPlayer: 3,
    useNewLeagues: false,
    matchFormat: MATCH_FORMAT.TRADITIONAL,
    includeDoubles: false,
  })

  // Form state for large-scale seed options
  const [largeSeedOptions, setLargeSeedOptions] = useState({
    tag: 'largescale',
    numPlayers: 40,
    divisionsPerLeague: 5,
  })

  // UI state
  const [showSeedDetails, setShowSeedDetails] = useState(false)
  const [openPanel, setOpenPanel] = useState('seed') // 'seed' | 'large-seed' | 'demo' | 'skill-demo' | 'reset' | 'cleanup'
  const [demoStopAfter, setDemoStopAfter] = useState('')
  const [lastDemoToolName, setLastDemoToolName] = useState(null)
  const [skillDemoStopAfter, setSkillDemoStopAfter] = useState('')
  const [lastSkillDemoToolName, setLastSkillDemoToolName] = useState(null)

  const DEMO_STAGE_LABELS = {
    'demo-register-players': 'Register Players',
    'demo-assign-teams': 'Assign Teams',
    'demo-open-proposals': 'Open Proposals',
    'demo-accept-proposal': 'Accept Proposal',
    'demo-enter-scores': 'Enter Scores',
  }

  const SKILL_DEMO_STAGE_LABELS = {
    'api-create-tiered-league': 'Create Tiered League',
    'api-create-divisions': 'Create Divisions',
    'api-register-players': 'Register Players',
    'api-manage-divisions': 'Manage Divisions',
    'api-activate-regular-season': 'Activate Regular Season',
    'api-open-regular-season-scheduling': 'Open Regular Season Scheduling',
    'api-schedule-regular-season-matches': 'Schedule Regular Season Matches',
    'api-enter-regular-season-scores': 'Enter Regular Season Scores',
    'api-start-playoffs': 'Start Playoffs',
    'api-schedule-playoff-matches': 'Schedule Playoff Matches',
    'api-enter-playoff-scores': 'Enter Playoff Scores',
  }

  // Request body builders for each tool
  const buildSeedBody = useCallback(
    (overrides = {}) => ({
      adminEmail: user?.email,
      ...seedOptions,
      ...overrides,
    }),
    [user, seedOptions],
  )

  const buildResetBody = useCallback(
    (overrides = {}) => ({
      tag: seedOptions.tag,
      ...overrides,
    }),
    [seedOptions.tag],
  )

  const buildLargeSeedBody = useCallback(
    (overrides = {}) => ({
      ...largeSeedOptions,
      ...overrides,
    }),
    [largeSeedOptions],
  )

  const buildLargeResetBody = useCallback(
    (overrides = {}) => ({
      tag: largeSeedOptions.tag,
      ...overrides,
    }),
    [largeSeedOptions.tag],
  )

  const buildExpireProposalsBody = useCallback(
    (overrides = {}) => ({
      batchSize: 100,
      ...overrides,
    }),
    [],
  )

  const buildDemoRunAllBody = useCallback(
    (overrides = {}) => ({
      ...(demoStopAfter ? { stopAfter: demoStopAfter } : {}),
      ...overrides,
    }),
    [demoStopAfter],
  )

  const buildSkillDemoRunAllBody = useCallback(
    (overrides = {}) => ({
      ...(skillDemoStopAfter ? { stopAfter: skillDemoStopAfter } : {}),
      ...overrides,
    }),
    [skillDemoStopAfter],
  )

  // Admin tool hooks - replaces 6 useState declarations
  const seedTool = useAdminTool('seed', {
    buildRequestBody: buildSeedBody,
  })
  const resetTool = useAdminTool('reset', {
    buildRequestBody: buildResetBody,
  })
  const cleanupTool = useAdminTool('expire-proposals', {
    buildRequestBody: buildExpireProposalsBody,
  })
  const clearAllTool = useAdminTool('clear-all-seeds')
  const largeSeedTool = useAdminTool('seed-large-scale', {
    buildRequestBody: buildLargeSeedBody,
  })
  const largeResetTool = useAdminTool('reset-large-scale', {
    buildRequestBody: buildLargeResetBody,
  })
  const demoSeedTool = useAdminTool('demo-seed')
  const demoResetTool = useAdminTool('demo-reset')
  const demoRunAllTool = useAdminTool('demo-run-all', {
    buildRequestBody: buildDemoRunAllBody,
  })
  const skillDemoSeedTool = useAdminTool('skill-demo-seed')
  const skillDemoResetTool = useAdminTool('skill-demo-reset')
  const skillDemoRunAllTool = useAdminTool('skill-demo-run-all', {
    buildRequestBody: buildSkillDemoRunAllBody,
  })

  const activeDemoTool =
    lastDemoToolName === 'seed'
      ? demoSeedTool
      : lastDemoToolName === 'reset'
        ? demoResetTool
        : lastDemoToolName === 'run-all'
          ? demoRunAllTool
          : null

  const activeSkillDemoTool =
    lastSkillDemoToolName === 'seed'
      ? skillDemoSeedTool
      : lastSkillDemoToolName === 'reset'
        ? skillDemoResetTool
        : lastSkillDemoToolName === 'run-all'
          ? skillDemoRunAllTool
          : null

  if (authLoading) return <p>Loading...</p>
  if (!isAdmin) return <p>Access denied. You are not an admin.</p>

  return (
    <PageLayout title="Database Tools" noCard={true} heroVariant="compact">
      <div className="container py-3 admin-db">
        <div className="row g-3">
          <AdminToolPanel
            title="Seed Database"
            subtitle="Populate test data"
            isOpen={openPanel === 'seed'}
            onToggle={() => setOpenPanel(openPanel === 'seed' ? '' : 'seed')}
            stdout={seedTool.stdout}
            stderr={seedTool.stderr}
            variant="seed"
          >
            <p className="card-text">
              Populate sample leagues, players, and matches with customizable
              options.
            </p>
            <SeedOptionsForm
              options={seedOptions}
              onChange={setSeedOptions}
              variant="normal"
            />
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ActionButton
                label="Run Seed"
                onClick={() => seedTool.execute()}
                loading={seedTool.loading}
                disabled={false}
              />
              <ActionButton
                label="Simple Seed"
                onClick={() => seedTool.execute({ simple: true })}
                loading={seedTool.loading}
                disabled={false}
                variant="success"
              />
            </div>
            {seedTool.message && (
              <div className="small text-muted mb-2">{seedTool.message}</div>
            )}
            <div className="mb-2">
              <button
                className="btn btn-sm btn-outline-secondary w-100 btn-tall"
                onClick={() => setShowSeedDetails(!showSeedDetails)}
              >
                {showSeedDetails ? 'Hide details ▴' : 'Show details ▾'}
              </button>
            </div>
            {showSeedDetails && (
              <small className="text-muted mb-3 d-block">
                <strong>Simple Seed</strong> creates 12 easy test accounts:
                alice@gmail.com/alice, bob@gmail.com/bob, carol@gmail.com/carol,
                david@gmail.com/david, emma@gmail.com/emma,
                frank@gmail.com/frank, grace@gmail.com/grace,
                henry@gmail.com/henry, iris@gmail.com/iris, jack@gmail.com/jack,
                kelly@gmail.com/kelly, leo@gmail.com/leo
              </small>
            )}
          </AdminToolPanel>

          <AdminToolPanel
            title="Skill-Level API Harness"
            subtitle="Run tiered league scenario"
            isOpen={openPanel === 'skill-demo'}
            onToggle={() =>
              setOpenPanel(openPanel === 'skill-demo' ? '' : 'skill-demo')
            }
            stdout={activeSkillDemoTool?.stdout ?? ''}
            stderr={activeSkillDemoTool?.stderr ?? ''}
            variant="seed"
          >
            <p className="card-text">
              Run skill-level segmented league scripts over live HTTP endpoints
              to validate level-scoped regular season and playoff flows.
            </p>
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ActionButton
                label="Skill Harness Seed"
                onClick={() => {
                  setLastSkillDemoToolName('seed')
                  skillDemoSeedTool.execute()
                }}
                loading={skillDemoSeedTool.loading}
                variant="success"
              />
              <ConfirmActionButton
                label="Skill Harness Reset"
                confirmLabel="Confirm Skill Harness Reset"
                onConfirm={() => {
                  setLastSkillDemoToolName('reset')
                  skillDemoResetTool.execute()
                }}
                loading={skillDemoResetTool.loading}
                variant="danger"
              />
            </div>
            <div className="mb-2">
              <label
                className="form-label small"
                htmlFor="skill-demo-stop-after-select"
              >
                Run skill harness up to:
              </label>
              <select
                id="skill-demo-stop-after-select"
                className="form-select form-select-sm"
                value={skillDemoStopAfter}
                onChange={(e) => setSkillDemoStopAfter(e.target.value)}
              >
                <option value="">Full flow (all stages)</option>
                <option value="api-create-tiered-league">
                  Create Tiered League
                </option>
                <option value="api-create-divisions">Create Divisions</option>
                <option value="api-register-players">Register Players</option>
                <option value="api-manage-divisions">Manage Divisions</option>
                <option value="api-activate-regular-season">
                  Activate Regular Season
                </option>
                <option value="api-open-regular-season-scheduling">
                  Open Regular Season Scheduling
                </option>
                <option value="api-schedule-regular-season-matches">
                  Schedule Regular Season Matches
                </option>
                <option value="api-enter-regular-season-scores">
                  Enter Regular Season Scores
                </option>
                <option value="api-start-playoffs">Start Playoffs</option>
                <option value="api-schedule-playoff-matches">
                  Schedule Playoff Matches
                </option>
                <option value="api-enter-playoff-scores">
                  Enter Playoff Scores
                </option>
              </select>
            </div>
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ActionButton
                label={
                  skillDemoStopAfter
                    ? `Run Skill Harness → ${SKILL_DEMO_STAGE_LABELS[skillDemoStopAfter] ?? skillDemoStopAfter}`
                    : 'Run Full Skill Harness'
                }
                onClick={() => {
                  setLastSkillDemoToolName('run-all')
                  skillDemoRunAllTool.execute()
                }}
                loading={skillDemoRunAllTool.loading}
              />
            </div>
            {skillDemoSeedTool.message && (
              <div className="small text-muted mb-1">
                {skillDemoSeedTool.message}
              </div>
            )}
            {skillDemoResetTool.message && (
              <div className="small text-muted mb-1">
                {skillDemoResetTool.message}
              </div>
            )}
            {skillDemoRunAllTool.message && (
              <div className="small text-muted mb-1">
                {skillDemoRunAllTool.message}
              </div>
            )}
          </AdminToolPanel>

          <AdminToolPanel
            title="Large-Scale Seed"
            subtitle="Production-scale test data"
            isOpen={openPanel === 'large-seed'}
            onToggle={() =>
              setOpenPanel(openPanel === 'large-seed' ? '' : 'large-seed')
            }
            stdout={largeSeedTool.stdout || largeResetTool.stdout}
            stderr={largeSeedTool.stderr || largeResetTool.stderr}
            variant="seed"
          >
            <p className="card-text">
              Seed production-scale data (30-40+ players) for testing seating
              and league split logic.
            </p>
            <SeedOptionsForm
              options={largeSeedOptions}
              onChange={setLargeSeedOptions}
              variant="large"
            />
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ActionButton
                label="Run Large-Scale Seed"
                onClick={() => largeSeedTool.execute()}
                loading={largeSeedTool.loading}
                disabled={false}
              />
            </div>
            {largeSeedTool.message && (
              <div className="small text-muted mb-2">
                {largeSeedTool.message}
              </div>
            )}
            <small className="text-muted d-block mb-2">
              <strong>Default config:</strong> 40 players, 2 leagues, 5
              divisions per league (4 players each), 60 total matches.
            </small>

            {/* Reset Large-Scale Data */}
            <hr className="my-3" />
            <div className="mt-3">
              <h5 className="mb-2">Reset Large-Scale Data</h5>
              <p className="card-text text-muted small">
                Remove large-scale seeded data using the tag above.
              </p>
              <div className="d-grid gap-2 mb-2 admin-actions">
                <ConfirmActionButton
                  label="Reset Large-Scale Data"
                  confirmLabel="Confirm Reset"
                  onConfirm={() => largeResetTool.execute()}
                  loading={largeResetTool.loading}
                  variant="danger"
                />
              </div>
              {largeResetTool.message && (
                <div className="small text-muted mb-2">
                  {largeResetTool.message}
                </div>
              )}
            </div>
          </AdminToolPanel>

          <AdminToolPanel
            title="Doubles Demo Scripts"
            subtitle="Run HTTP demo stages"
            isOpen={openPanel === 'demo'}
            onToggle={() => setOpenPanel(openPanel === 'demo' ? '' : 'demo')}
            stdout={activeDemoTool?.stdout ?? ''}
            stderr={activeDemoTool?.stderr ?? ''}
            variant="seed"
          >
            <p className="card-text">
              Run demo scripts that exercise the doubles workflow over live HTTP
              endpoints on this running server.
            </p>
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ActionButton
                label="Demo Seed"
                onClick={() => {
                  setLastDemoToolName('seed')
                  demoSeedTool.execute()
                }}
                loading={demoSeedTool.loading}
                variant="success"
              />
              <ConfirmActionButton
                label="Demo Reset"
                confirmLabel="Confirm Demo Reset"
                onConfirm={() => {
                  setLastDemoToolName('reset')
                  demoResetTool.execute()
                }}
                loading={demoResetTool.loading}
                variant="danger"
              />
            </div>
            <div className="mb-2">
              <label
                className="form-label small"
                htmlFor="demo-stop-after-select"
              >
                Run flow up to:
              </label>
              <select
                id="demo-stop-after-select"
                className="form-select form-select-sm"
                value={demoStopAfter}
                onChange={(e) => setDemoStopAfter(e.target.value)}
              >
                <option value="">Full flow (all stages)</option>
                <option value="demo-register-players">Register Players</option>
                <option value="demo-assign-teams">Assign Teams</option>
                <option value="demo-open-proposals">Open Proposals</option>
                <option value="demo-accept-proposal">Accept Proposal</option>
                <option value="demo-enter-scores">Enter Scores</option>
              </select>
            </div>
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ActionButton
                label={
                  demoStopAfter
                    ? `Run Demo → ${DEMO_STAGE_LABELS[demoStopAfter]}`
                    : 'Run Full Demo'
                }
                onClick={() => {
                  setLastDemoToolName('run-all')
                  demoRunAllTool.execute()
                }}
                loading={demoRunAllTool.loading}
              />
            </div>
            {demoSeedTool.message && (
              <div className="small text-muted mb-1">
                {demoSeedTool.message}
              </div>
            )}
            {demoResetTool.message && (
              <div className="small text-muted mb-1">
                {demoResetTool.message}
              </div>
            )}
            {demoRunAllTool.message && (
              <div className="small text-muted mb-1">
                {demoRunAllTool.message}
              </div>
            )}
          </AdminToolPanel>

          <AdminToolPanel
            title="Reset Database"
            subtitle="Remove sample data"
            isOpen={openPanel === 'reset'}
            onToggle={() => setOpenPanel(openPanel === 'reset' ? '' : 'reset')}
            stdout={resetTool.stdout}
            stderr={resetTool.stderr}
            variant="reset"
          >
            <p className="card-text flex-grow-1">
              Remove previously-seeded sample data.
            </p>
            <div className="mb-3">
              <label className="form-label small">Tag</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={seedOptions.tag}
                onChange={(e) =>
                  setSeedOptions((prev) => ({ ...prev, tag: e.target.value }))
                }
              />
              <small className="text-muted">
                Must match the tag used when seeding
              </small>
            </div>
            <div className="d-grid gap-2 mb-2 admin-actions">
              <ConfirmActionButton
                label="Run Reset"
                confirmLabel="Confirm Reset"
                onConfirm={() => resetTool.execute()}
                loading={resetTool.loading}
                variant="danger"
              />
            </div>
            {resetTool.message && (
              <div className="small text-muted mb-2">{resetTool.message}</div>
            )}
            <small className="text-muted mb-3">
              Reset removes all seeded data created by Seed/Simple Seed.
            </small>

            {/* Divider */}
            <hr className="my-3" />

            {/* Clear All Seeds Section */}
            <div className="mt-3">
              <h5 className="mb-2">Clear All Seed Data</h5>
              <p className="card-text text-muted small">
                ⚠️ <strong>Warning:</strong> This removes ALL seeded data
                regardless of tag (including both normal and simple mode data).
                Use with caution!
              </p>
              <div className="d-grid gap-2 mb-2 admin-actions">
                <ConfirmActionButton
                  label="Clear All Seeds"
                  confirmLabel="⚠️ Confirm Clear ALL Seeds"
                  onConfirm={() => clearAllTool.execute()}
                  loading={clearAllTool.loading}
                  variant="danger"
                />
              </div>
              {clearAllTool.message && (
                <div className="small text-muted mb-2">
                  {clearAllTool.message}
                </div>
              )}
              {clearAllTool.stdout && (
                <pre
                  className="mt-2 small bg-light p-2 border rounded"
                  style={{ maxHeight: 120, overflow: 'auto' }}
                >
                  {clearAllTool.stdout}
                </pre>
              )}
              {clearAllTool.stderr && (
                <pre
                  className="mt-2 small bg-warning p-2 border rounded"
                  style={{ maxHeight: 120, overflow: 'auto' }}
                >
                  {clearAllTool.stderr}
                </pre>
              )}
            </div>
          </AdminToolPanel>

          <AdminToolPanel
            title="Cleanup Operations"
            subtitle="Remove expired data"
            isOpen={openPanel === 'cleanup'}
            onToggle={() =>
              setOpenPanel(openPanel === 'cleanup' ? '' : 'cleanup')
            }
            stdout={cleanupTool.stdout}
            stderr={cleanupTool.stderr}
            variant="cleanup"
          >
            <p className="card-text">
              Clear out expired or stale data to maintain database health.
            </p>

            <div className="cleanup-operations flex-grow-1">
              <div className="cleanup-operation mb-3 p-3 border rounded">
                <h6 className="mb-2">Expire Match Proposals</h6>
                <p className="small text-muted mb-2">
                  Mark pending match proposals that have passed their expiration
                  time as expired.
                </p>
                <div className="d-grid gap-2 admin-actions">
                  <ActionButton
                    label="Expire Proposals"
                    variant="warning"
                    onClick={() => cleanupTool.execute()}
                    loading={cleanupTool.loading}
                    disabled={false}
                  />
                </div>
                {cleanupTool.message && (
                  <div className="small text-muted mt-1">
                    {cleanupTool.message}
                  </div>
                )}
              </div>

              {/* Future cleanup operations can be added here */}
              <div className="cleanup-operation mb-3 p-3 border rounded bg-light">
                <h6 className="mb-2 text-muted">Future Cleanup Operations</h6>
                <p className="small text-muted mb-0">
                  Additional cleanup operations (e.g., remove old notifications,
                  clean up orphaned records) can be added here.
                </p>
              </div>
            </div>
          </AdminToolPanel>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminDatabaseTools
